<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useForm } from '@inertiajs/vue3'
import Breadcrumbs from '@/Components/Breadcrumbs.vue'
import ApplicationHead from '@/Components/Layout/ApplicationHead.vue'
import { useHelpers } from '@/Composables/helpers'
import { BuildingOfficeIcon, UserGroupIcon } from '@heroicons/vue/24/outline'
import FacebookIcon from '@/Components/Icons/FacebookIcon.vue'
import InstagramIcon from '@/Components/Icons/InstagramIcon.vue'
import Input from '@/Components/Forms/Input.vue'
import Textarea from '@/Components/Forms/Textarea.vue'
import FormGroup from '@/Components/Forms/FormGroup.vue'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import Button from '@/Components/Button.vue'
import { LinkType } from '@enums/link-type.enum'
import type ziggyRoute from 'ziggy-js'
import { inject } from 'vue'

const props = defineProps<{
    honeypot: {
        enabled: boolean,
        nameFieldName: string,
        validFromFieldName: string,
        encryptedValidFrom: string,
    },
    product?: {
        id: number,
        sku: string,
        title: string,
    },
}>()


const route = inject<typeof ziggyRoute>('route')
const { asset, socialLink } = useHelpers()
const { t } = useI18n()

const form = useForm({
    [props.honeypot.nameFieldName]: '',
    [props.honeypot.validFromFieldName]: props.honeypot.encryptedValidFrom,
    name: '',
    email: '',
    message: '',
})

if (props.product) {
    form.message = `When will the product be available?\n\n${props.product.title} (ID: ${props.product.id}, SKU: ${props.product.sku})`
}

const send = () => {
    form.post(route!('contact'), {
        onSuccess: () => {
            form.reset()
        },
    })
}
</script>

<template>
    <div>
        <ApplicationHead :title="t('pages.contacts.title')" :description="t('pages.contacts.description')">
            <meta property="og:type" content="website">
            <meta property="og:image" :content="asset('/images/social-logo.png')">
        </ApplicationHead>

        <Breadcrumbs />

        <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <h1 class="text-4xl font-bold tracking-tight text-gray-900">{{ t('pages.contacts.title') }}</h1>
            <p class="mt-2 text-lg leading-8 text-gray-600">{{ t('pages.contacts.description') }}</p>

            <div class="flex flex-col gap-16 mt-16 sm:gap-y-20 lg:flex-row">
                <aside class="lg:w-96">
                    <div class="-my-8 divide-y divide-gray-200">
                        <div class="py-8">
                            <div class="flex items-center">
                                <BuildingOfficeIcon class="w-6 h-6 mr-4 text-indigo-600" />
                                <h2 class="text-base font-semibold leading-7 text-gray-900">{{ t('pages.contacts.company') }}</h2>
                            </div>

                            <p class="mt-6 leading-7 text-gray-600">
                                Calypso OÜ<br>
                                Reg. nr: 14794707<br>
                                KMKR: EE102185506<br><br>
                                Õismäe tee 57A, Tallinn 13515<br>
                                Estonia
                            </p>
                            <p class="mt-4">
                                <a href="mailto:info@calypso.ee" class="text-sm font-semibold leading-6 text-indigo-600">info@calypso.ee</a>
                            </p>
                        </div>

                        <div class="py-8">
                            <div class="flex items-center">
                                <UserGroupIcon class="w-6 h-6 mr-4 text-indigo-600" />
                                <h2 class="text-base font-semibold leading-7 text-gray-900">{{ t('pages.contacts.follow-us') }}</h2>
                            </div>

                            <p class="mt-6 leading-7 text-gray-600">{{ t('pages.contacts.follow-us-description') }}</p>
                            <ul class="flex mt-4 -mx-4">
                                <li>
                                    <a class="block px-4 py-2 text-gray-600 hover:text-gray-700" :href="socialLink(LinkType.Facebook)" target="blank" aria-label="Facebook">
                                        <FacebookIcon class="w-4 h-4" />
                                    </a>
                                </li>

                                <li class="-ml-4">
                                    <a class="block px-4 py-2 text-gray-600 hover:text-gray-700" :href="socialLink(LinkType.Instagram)" target="blank" aria-label="Instagram">
                                        <InstagramIcon class="w-4 h-4" />
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </aside>

                <form action="#" method="POST" class="lg:flex-auto">
                    <div v-if="form.wasSuccessful" class="p-4 mb-6 rounded-md bg-green-50" role="alert">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <CheckCircleIcon class="w-5 h-5 text-green-400" aria-hidden="true" />
                            </div>

                            <div class="ml-3">
                                <p class="text-sm font-medium text-green-800">{{ $page.props.flash?.alert_message }}</p>
                            </div>
                        </div>
                    </div>

                    <div v-if="honeypot.enabled" :name="`${honeypot.nameFieldName}_wrap`" class="hidden">
                        <input :id="honeypot.nameFieldName" v-model="form[honeypot.nameFieldName]" type="text" :name="honeypot.nameFieldName" />
                        <input v-model="form[honeypot.validFromFieldName]" type="text" :name="honeypot.validFromFieldName" />
                    </div>

                    <div class="space-y-6">
                        <FormGroup :label="`${t('pages.contacts.form.name')} *`" name="name" :error="form.errors.name">
                            <Input id="name" v-model="form.name" type="text" name="name" class="w-full" />
                        </FormGroup>

                        <FormGroup :label="`${t('pages.contacts.form.email')} *`" name="email" :error="form.errors.email">
                            <Input id="email" v-model="form.email" type="email" name="email" class="w-full" />
                        </FormGroup>

                        <FormGroup :label="`${t('pages.contacts.form.message')} *`" name="message" :error="form.errors.message">
                            <Textarea id="message" v-model="form.message" name="message" rows="4" class="w-full" />
                        </FormGroup>
                    </div>

                    <div class="mt-10">
                        <Button type="button" class="w-full" :loading="form.processing" @click="send">{{ t('pages.contacts.form.send-message') }}</button>
                    </div>

                    <p class="mt-4 text-sm leading-6 text-gray-500">{{ t('pages.contacts.form.gdpr-note') }}</p>
                </form>
            </div>
        </section>
    </div>
</template>
